import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { SchoolSelect, isMaster } from "../components/SchoolSelect";

const empty = {
  class_name: "", section: "", subject: "", date: new Date().toISOString().slice(0, 10),
  period: "", topic: "", objectives: "", methodology: "", teaching_aids: "",
  activities: "", homework: "", assessment: "",
};

const statusLabel = {
  submitted: { c: "bg-amber-100 text-amber-800", t: "Awaiting Review", Icon: Clock },
  approved: { c: "bg-emerald-100 text-emerald-800", t: "Approved", Icon: CheckCircle },
  needs_revision: { c: "bg-rose-100 text-rose-800", t: "Needs Revision", Icon: XCircle },
};

export default function LessonPlans() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/lesson-plans"); setRows(data); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/lesson-plans", form);
      toast.success("Lesson plan submitted");
      setForm(empty); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const review = async (id, status) => {
    const note = status === "needs_revision" ? window.prompt("Note for teacher?") || "" : "";
    try {
      await api.patch(`/lesson-plans/${id}/review`, { status, reviewer_note: note });
      toast.success("Review saved"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canReview = ["admin", "principal"].includes(user.role);
  const canSubmit = ["teacher", "admin", "principal"].includes(user.role);

  return (
    <div className="space-y-6" data-testid="lesson-plans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black">Lesson Plans</h1>
          <p className="text-zinc-500 mt-1">Submit, review and track daily lesson plans.</p>
        </div>
        {canSubmit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-plan-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white"><Plus size={16} className="mr-1" />New Plan</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New Lesson Plan</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div className="grid grid-cols-4 gap-3">
                  <div><Label>Class</Label><Input required value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} /></div>
                  <div><Label>Section</Label><Input required value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} /></div>
                  <div><Label>Subject</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
                  <div><Label>Period</Label><Input required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="1" /></div>
                </div>
                <div><Label>Date</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                {isMaster(user) && <SchoolSelect value={form.school_id} onChange={(v) => setForm({ ...form, school_id: v })} testId="plan-school-select" />}
                <div><Label>Topic</Label><Input required value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="plan-topic-input" /></div>
                <div><Label>Learning Objectives</Label><Textarea required rows={2} value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} /></div>
                <div><Label>Methodology</Label><Textarea required rows={2} value={form.methodology} onChange={(e) => setForm({ ...form, methodology: e.target.value })} /></div>
                <div><Label>Teaching Aids</Label><Input required value={form.teaching_aids} onChange={(e) => setForm({ ...form, teaching_aids: e.target.value })} /></div>
                <div><Label>Activities</Label><Textarea required rows={2} value={form.activities} onChange={(e) => setForm({ ...form, activities: e.target.value })} /></div>
                <div><Label>Homework</Label><Input required value={form.homework} onChange={(e) => setForm({ ...form, homework: e.target.value })} /></div>
                <div><Label>Assessment</Label><Input required value={form.assessment} onChange={(e) => setForm({ ...form, assessment: e.target.value })} /></div>
                <Button type="submit" className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="plan-submit-btn">Submit Plan</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {rows.map((p) => {
          const S = statusLabel[p.status] || statusLabel.submitted;
          return (
            <Card key={p.id} className="p-5 border-zinc-200" data-testid={`plan-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide text-zinc-500">{p.subject} · {p.class_name}-{p.section} · P{p.period}</div>
                  <h3 className="font-display text-lg font-bold mt-1">{p.topic}</h3>
                  <div className="text-xs text-zinc-500 mt-0.5">{p.date} · {p.teacher_name}</div>
                </div>
                <span className={`text-[10px] px-2 py-1 rounded font-semibold uppercase tracking-wide inline-flex items-center gap-1 ${S.c}`}>
                  <S.Icon size={12} weight="bold" />{S.t}
                </span>
              </div>
              <div className="mt-3 text-sm space-y-1">
                <div><span className="font-semibold">Objectives: </span><span className="text-zinc-600">{p.objectives}</span></div>
                <div><span className="font-semibold">Homework: </span><span className="text-zinc-600">{p.homework}</span></div>
                {p.reviewer_note && <div className="text-rose-700 text-xs mt-2">Note: {p.reviewer_note}</div>}
              </div>
              {canReview && p.status === "submitted" && (
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => review(p.id, "approved")} data-testid={`approve-${p.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => review(p.id, "needs_revision")} data-testid={`revise-${p.id}`}>Needs Revision</Button>
                </div>
              )}
            </Card>
          );
        })}
        {rows.length === 0 && <div className="text-zinc-500 text-sm">No lesson plans yet.</div>}
      </div>
    </div>
  );
}
